import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faExclamationTriangle,
  faCheckCircle,
  faPlus,
  faWrench,
  faSignInAlt,
  faSignOutAlt,
  faFileUpload,
  faFileAlt,
  faQuoteRight,
  faPlay,
  faEye,
  faBuilding,
  faUser,
  faBell,
  faListAlt,
  faArrowRight,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import { useParams, useOutletContext } from 'react-router-dom';
import { getApiEndpoint } from '../../../config/api';
import { getAuthHeader } from '../../../utils/auth';
import './ActivityPage.css';

/* ── Icon + class mapping for action types ────────────────────── */
const ACTION_CONFIG = {
  check_in:          { icon: faSignInAlt,         cls: 'icon-added',     pill: 'pill-added',     label: 'Check-in' },
  check_out:         { icon: faSignOutAlt,         cls: 'icon-default',   pill: 'pill-default',   label: 'Check-out' },
  document_uploaded: { icon: faFileUpload,         cls: 'icon-added',     pill: 'pill-added',     label: 'Uploaded' },
  document_viewed:   { icon: faFileAlt,            cls: 'icon-default',   pill: 'pill-default',   label: 'Viewed' },
  quote_submitted:   { icon: faQuoteRight,         cls: 'icon-scheduled', pill: 'pill-scheduled', label: 'Submitted' },
  quote_approved:    { icon: faCheckCircle,        cls: 'icon-passed',    pill: 'pill-passed',    label: 'Approved' },
  quote_declined:    { icon: faExclamationTriangle,cls: 'icon-failed',    pill: 'pill-failed',    label: 'Declined' },
  job_started:       { icon: faPlay,               cls: 'icon-added',     pill: 'pill-added',     label: 'Started' },
  job_completed:     { icon: faCheckCircle,        cls: 'icon-passed',    pill: 'pill-passed',    label: 'Completed' },
  property_viewed:   { icon: faEye,                cls: 'icon-default',   pill: 'pill-default',   label: 'Viewed' },
  property_edited:   { icon: faBuilding,           cls: 'icon-scheduled', pill: 'pill-scheduled', label: 'Edited' },
  access_assigned:   { icon: faUser,               cls: 'icon-added',     pill: 'pill-added',     label: 'Assigned' },
  access_removed:    { icon: faUser,               cls: 'icon-failed',    pill: 'pill-failed',    label: 'Removed' },
  property_created:  { icon: faBuilding,           cls: 'icon-added',     pill: 'pill-added',     label: 'Created' },
  inspection_passed: { icon: faCheckCircle,        cls: 'icon-passed',    pill: 'pill-passed',    label: 'Passed' },
  inspection_failed: { icon: faExclamationTriangle,cls: 'icon-failed',    pill: 'pill-failed',    label: 'Failed' },
  maintenance_scheduled: { icon: faWrench,         cls: 'icon-scheduled', pill: 'pill-scheduled', label: 'Scheduled' },
  asset_added:       { icon: faPlus,               cls: 'icon-added',     pill: 'pill-added',     label: 'Added' },
};

const DEFAULT_CONFIG = { icon: faBell, cls: 'icon-default', pill: 'pill-default', label: 'Activity' };

/* ── Action type filter labels ────────────────────────────────── */
const ACTION_TYPE_LABELS = {
  check_in: 'Check-in',
  check_out: 'Check-out',
  document_uploaded: 'Document uploaded',
  document_viewed: 'Document viewed',
  quote_submitted: 'Quote submitted',
  quote_approved: 'Quote approved',
  quote_declined: 'Quote declined',
  job_started: 'Job started',
  job_completed: 'Job completed',
  property_viewed: 'Property viewed',
  property_edited: 'Property edited',
  access_assigned: 'Access assigned',
  access_removed: 'Access removed',
  property_created: 'Property created',
};

/* ── Date grouping helper ─────────────────────────────────────── */
function getDateGroup(timestamp) {
  if (!timestamp) return 'Earlier';
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (itemDate.getTime() === today.getTime()) return 'Today';
  if (itemDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return 'Earlier';
}

function formatTime(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}

/* ── Skeleton loader ──────────────────────────────────────────── */
function SkeletonItems({ count = 5 }) {
  return Array.from({ length: count }, (_, i) => (
    <div className="activity-skeleton-item" key={i} aria-hidden="true">
      <div className="skeleton-icon skeleton-pulse" />
      <div className="skeleton-body">
        <div className="skeleton-line skeleton-line-long skeleton-pulse" />
        <div className="skeleton-line skeleton-line-short skeleton-pulse" />
        <div className="skeleton-line skeleton-line-xs skeleton-pulse" />
      </div>
    </div>
  ));
}

/* ══════════════════════════════════════════════════════════════════
   ActivityPage component
   ══════════════════════════════════════════════════════════════════ */
const ActivityPage = () => {
  const { id: propertyId } = useParams();
  const outletContext = useOutletContext() || {};
  const propertyName = outletContext.property?.name || '';

  /* ── State ────────────────────────────────────────────────────── */
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  /* ── Fetch logs from real API ─────────────────────────────────── */
  const fetchLogs = async () => {
    if (!propertyId) return;
    setLoading(true);
    setError(null);
    try {
      const url = getApiEndpoint(`/api/properties/${propertyId}/logs`) +
        (actionFilter !== 'all' ? `?actionType=${encodeURIComponent(actionFilter)}` : '');
      const res = await fetch(url, { headers: getAuthHeader() });
      if (!res.ok) throw new Error('Failed to load activity');
      const data = await res.json();
      setLogs(data.logs || data || []);
    } catch (e) {
      console.error('Activity fetch error:', e);
      setError('Could not load activity for this property.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [propertyId, actionFilter]);

  /* ── Search filter (client-side) ──────────────────────────────── */
  const filteredLogs = useMemo(() => {
    if (!searchQuery.trim()) return logs;
    const q = searchQuery.toLowerCase();
    return logs.filter(log =>
      (log.description || '').toLowerCase().includes(q) ||
      (log.user_name || '').toLowerCase().includes(q) ||
      (log.action_type || '').toLowerCase().includes(q)
    );
  }, [logs, searchQuery]);

  /* ── Group by date ────────────────────────────────────────────── */
  const groupedLogs = useMemo(() => {
    const groups = {};
    for (const log of filteredLogs) {
      const group = getDateGroup(log.timestamp);
      if (!groups[group]) groups[group] = [];
      groups[group].push(log);
    }
    // Return in order: Today, Yesterday, Earlier
    const ordered = [];
    for (const label of ['Today', 'Yesterday', 'Earlier']) {
      if (groups[label]) ordered.push({ label, items: groups[label] });
    }
    return ordered;
  }, [filteredLogs]);

  /* ── Distinct action types for filter dropdown ────────────────── */
  const availableTypes = useMemo(() => {
    const types = new Set(logs.map(l => l.action_type).filter(Boolean));
    return [...types].sort();
  }, [logs]);

  /* ── Render ───────────────────────────────────────────────────── */
  return (
    <div className="activity-page">
      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="activity-page-header">
        <h1>Activity</h1>
        {propertyName && (
          <p className="activity-page-subtitle">{propertyName}</p>
        )}
      </div>

      {/* ── Error ──────────────────────────────────────────────── */}
      {error && (
        <div className="activity-error" role="alert">
          <FontAwesomeIcon icon={faExclamationTriangle} aria-hidden="true" />
          <span>{error}</span>
          <button className="activity-error-retry" onClick={fetchLogs}>
            <FontAwesomeIcon icon={faSyncAlt} /> Retry
          </button>
        </div>
      )}

      {/* ── Filter bar ─────────────────────────────────────────── */}
      <div className="activity-filter-bar">
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          className="activity-filter-select"
          aria-label="Filter by activity type"
        >
          <option value="all">All activity</option>
          {availableTypes.map(type => (
            <option key={type} value={type}>
              {ACTION_TYPE_LABELS[type] || type.replace(/_/g, ' ')}
            </option>
          ))}
        </select>

        <div className="activity-search-wrap">
          <FontAwesomeIcon icon={faSearch} className="search-icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search activity..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="activity-search-input"
            aria-label="Search activity"
          />
        </div>

        <span className="activity-entry-count">
          {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>

      {/* ── Loading skeleton ───────────────────────────────────── */}
      {loading && <SkeletonItems count={6} />}

      {/* ── Empty state ────────────────────────────────────────── */}
      {!loading && !error && filteredLogs.length === 0 && (
        <div className="activity-empty">
          <FontAwesomeIcon icon={faListAlt} className="activity-empty-icon" aria-hidden="true" />
          <h3>No recent activity</h3>
          <p>
            {searchQuery
              ? 'No results match your search. Try a different keyword.'
              : 'Activity appears here when actions are taken on this property — check-ins, document uploads, inspections, and more.'}
          </p>
        </div>
      )}

      {/* ── Activity feed ──────────────────────────────────────── */}
      {!loading && filteredLogs.length > 0 && (
        <div role="feed" aria-label="Property activity feed">
          {groupedLogs.map(group => (
            <section className="activity-date-group" key={group.label}>
              <h2 className="activity-date-label">{group.label}</h2>
              <ul className="activity-feed" role="list">
                {group.items.map(log => {
                  const config = ACTION_CONFIG[log.action_type] || DEFAULT_CONFIG;
                  return (
                    <li key={log.id} className="activity-item">
                      <div className={`activity-item-icon ${config.cls}`} aria-hidden="true">
                        <FontAwesomeIcon icon={config.icon} />
                      </div>
                      <div className="activity-item-body">
                        <p className="activity-item-message">
                          {log.description || ACTION_TYPE_LABELS[log.action_type] || log.action_type || 'Activity'}
                        </p>
                        <div className="activity-item-meta">
                          <span className={`activity-status-pill ${config.pill}`}>
                            {config.label}
                          </span>
                          {log.user_name && (
                            <>
                              <span className="meta-sep" aria-hidden="true">&middot;</span>
                              <span>{log.user_name}</span>
                            </>
                          )}
                          <span className="meta-sep" aria-hidden="true">&middot;</span>
                          <time dateTime={log.timestamp ? new Date(log.timestamp).toISOString() : undefined}>
                            {formatTime(log.timestamp)}
                          </time>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityPage;
